import { useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useAdmin } from '../../hooks/useAdmin'

type Variant = 'navbar' | 'early-signals'

/**
 * Admin-only tenant preview control. Selecting a company loads that tenant's
 * logo/settings via AuthContext.viewAsCompanyId + CompanyContext.
 */
export default function AdminViewAsSelect({ variant = 'navbar' }: { variant?: Variant }) {
  const { isAdmin, viewAsCompanyId, setViewAsCompanyId } = useAuth()
  const { companies } = useAdmin()

  const activeCompanies = useMemo(
    () =>
      (companies ?? [])
        .filter((c) => c.status === 'active')
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [companies]
  )

  if (!isAdmin) return null

  const isPreview = !!viewAsCompanyId
  const selectClass =
    variant === 'early-signals'
      ? 'text-xs font-semibold rounded-full border border-es-hairline bg-es-white text-es-deep px-3 py-1.5 max-w-[11rem] truncate focus:outline-none focus:border-es-signal'
      : 'text-xs font-semibold rounded-full border-2 border-amber-400 bg-amber-50 text-gray-900 px-3 py-1.5 max-w-[11rem] truncate focus:outline-none focus:border-amber-500'

  return (
    <label className="flex items-center gap-1.5 shrink-0" title="Preview another company's branding and Early Signals settings">
      <span
        className={
          variant === 'early-signals'
            ? 'text-[11px] font-semibold uppercase tracking-wide text-es-muted whitespace-nowrap'
            : 'text-[11px] font-bold uppercase tracking-wide text-amber-700 whitespace-nowrap'
        }
      >
        View as
      </span>
      <select
        aria-label="View as company"
        className={selectClass}
        value={viewAsCompanyId ?? ''}
        onChange={(e) => setViewAsCompanyId(e.target.value || null)}
      >
        <option value="">My company / default</option>
        {activeCompanies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      {isPreview && (
        <span
          className={
            variant === 'early-signals'
              ? 'hidden sm:inline text-[10px] font-semibold text-es-signal'
              : 'hidden sm:inline text-[10px] font-bold text-amber-700'
          }
        >
          Preview
        </span>
      )}
    </label>
  )
}
